import type { MutableRefObject, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import { useObservable } from 'observable-hooks';
import { schema } from '../../schema';
import { MenuWithRouter } from '../menu/MenuWithRouter';
import { createWalkthroughManager } from '../../walkthrough/walkthrough';

interface WebsiteProps {
    children: ReactNode;
}

// NOTE: This is not the real website. This is a mocked website that mimics the Django
// website. It is basically the React menu and the PageContainer merged into one component
// to allow for writing Storybook tests that need to navigate through pages via the menu, the
// walkthrough tests for example.
export function Website({ children }: WebsiteProps) {
    const location = useLocation();
    const navigate = useNavigate();

    const locationRef: MutableRefObject<Location | undefined> = useRef();

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    // Subject that can be used by the walkthrough to fold open menu paths.
    const forceMenuOpenById$ = useObservable(
        () => new BehaviorSubject<string | undefined>(undefined)
    );

    useEffect(() => {
        const demoMeasuringPoint = schema.demoMeasuringPoints.vibration;
        if (!demoMeasuringPoint) {
            throw new Error(
                'No demo vibration measuring point configured in schema.demoMeasuringPoints.'
            );
        }

        const manager = createWalkthroughManager();
        manager.loadSchema({
            demoMeasuringPoint,
            forceMenuOpenById$,
            getLocation(): Location | undefined {
                return locationRef.current;
            },
            navigate,
        });
    }, [forceMenuOpenById$, navigate]);

    return (
        <>
            <MenuWithRouter forceMenuOpenById$={forceMenuOpenById$} spaMode={true} />
            {children}
        </>
    );
}
